import type { AppearanceTheme, ResolvedAppearanceMode } from "./types";

export const SOLAR_BLEND_VARIABLES = [
  "canvas",
  "background",
  "foreground",
  "chrome",
  "chrome-foreground",
  "chrome-muted",
  "chrome-muted-foreground",
  "chrome-border",
  "card",
  "card-foreground",
  "popover",
  "popover-foreground",
  "muted",
  "muted-foreground",
  "border",
  "input",
  "primary",
  "primary-foreground",
  "secondary",
  "selected",
  "selected-foreground",
  "ring",
] as const;

export type SolarBlendVariable = (typeof SOLAR_BLEND_VARIABLES)[number];

export type RgbTriplet = [number, number, number];

export type SolarThemeTokens = Record<SolarBlendVariable, RgbTriplet>;

type ThemeTokenKey = `${AppearanceTheme}:${ResolvedAppearanceMode}`;

const themeTokenCache = new Map<ThemeTokenKey, SolarThemeTokens>();

function parseRgbTriplet(value: string): RgbTriplet | null {
  const parts = value.trim().split(/\s+/).map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return null;
  }

  return [parts[0], parts[1], parts[2]];
}

function formatRgbTriplet(value: RgbTriplet): string {
  return `${value[0]} ${value[1]} ${value[2]}`;
}

function matchesThemeSelector(
  selectorText: string,
  theme: AppearanceTheme,
  mode: ResolvedAppearanceMode
): boolean {
  if (!selectorText.includes(`data-theme="${theme}"`)) {
    return false;
  }

  if (mode === "dark") {
    return selectorText.includes(":root.dark");
  }

  return selectorText.includes(":root") && !selectorText.includes(":root.dark");
}

function readThemeTokensFromStylesheets(
  theme: AppearanceTheme,
  mode: ResolvedAppearanceMode
): SolarThemeTokens | null {
  if (typeof document === "undefined") {
    return null;
  }

  const tokens = {} as SolarThemeTokens;
  let matchedRule = false;

  // A theme+mode selector can appear in more than one rule (a base block
  // plus a narrower override block, e.g. magic-hour's light-mode override).
  // Merge every matching rule in source order - later rules win, same as
  // the cascade - instead of stopping at the first match, which silently
  // dropped variables only defined in a later rule.
  for (const sheet of document.styleSheets) {
    let cssRules: CSSRuleList;

    try {
      cssRules = sheet.cssRules;
    } catch {
      continue;
    }

    for (const rule of cssRules) {
      if (!(rule instanceof CSSStyleRule)) {
        continue;
      }

      if (!matchesThemeSelector(rule.selectorText, theme, mode)) {
        continue;
      }

      for (const variable of SOLAR_BLEND_VARIABLES) {
        const rawValue = rule.style.getPropertyValue(`--${variable}`).trim();
        const parsed = parseRgbTriplet(rawValue);
        if (parsed) {
          tokens[variable] = parsed;
        }
      }

      matchedRule = true;
    }
  }

  if (!matchedRule) {
    return null;
  }

  return tokens;
}

export function getSolarThemeTokens(
  theme: AppearanceTheme,
  mode: ResolvedAppearanceMode
): SolarThemeTokens | null {
  const cacheKey: ThemeTokenKey = `${theme}:${mode}`;
  const cached = themeTokenCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const tokens = readThemeTokensFromStylesheets(theme, mode);
  if (!tokens) {
    return null;
  }

  themeTokenCache.set(cacheKey, tokens);
  return tokens;
}

export function lerpRgbTriplet(from: RgbTriplet, to: RgbTriplet, progress: number): RgbTriplet {
  const mix = Math.min(Math.max(progress, 0), 1);

  return [
    Math.round(from[0] + (to[0] - from[0]) * mix),
    Math.round(from[1] + (to[1] - from[1]) * mix),
    Math.round(from[2] + (to[2] - from[2]) * mix),
  ];
}

export function easeInOutCubic(progress: number): number {
  const mix = Math.min(Math.max(progress, 0), 1);

  if (mix < 0.5) {
    return 4 * mix * mix * mix;
  }

  return 1 - Math.pow(-2 * mix + 2, 3) / 2;
}

export function blendSolarThemeTokens(
  from: SolarThemeTokens,
  to: SolarThemeTokens,
  progress: number
): SolarThemeTokens {
  const easedProgress = easeInOutCubic(progress);
  const blended = {} as SolarThemeTokens;

  for (const variable of SOLAR_BLEND_VARIABLES) {
    const fromValue = from[variable];
    const toValue = to[variable];
    // A variable can be genuinely absent for a given theme/mode (not every
    // preset defines every blend variable). Fall back to whichever side is
    // present rather than crashing the whole app on a partial stylesheet read.
    if (fromValue && toValue) {
      blended[variable] = lerpRgbTriplet(fromValue, toValue, easedProgress);
    } else if (fromValue || toValue) {
      blended[variable] = (fromValue ?? toValue) as RgbTriplet;
    }
  }

  return blended;
}

export function applySolarThemeTokenOverrides(root: HTMLElement, tokens: SolarThemeTokens): void {
  for (const variable of SOLAR_BLEND_VARIABLES) {
    const value = tokens[variable];
    if (value) {
      root.style.setProperty(`--${variable}`, formatRgbTriplet(value));
    }
  }

  root.dataset.solarBlend = "active";
}

export function clearSolarThemeTokenOverrides(root: HTMLElement): void {
  for (const variable of SOLAR_BLEND_VARIABLES) {
    root.style.removeProperty(`--${variable}`);
  }

  delete root.dataset.solarBlend;
}

export function clearSolarThemeTokenCache(): void {
  themeTokenCache.clear();
}
